import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API_URL from "../../apiConfig";
import AdminHeader from "../../components/AdminHeader";

export default function AdminMemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [duration, setDuration] = useState("1_month");
  const [actionLoading, setActionLoading] = useState(false);
  const [showConnectionHistory, setShowConnectionHistory] = useState(false);

  useEffect(() => {
    loadMember();
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadMember() {
    try {
      const session = JSON.parse(localStorage.getItem("zedro_session"));
      const res = await fetch(`${API_URL}/api/admin/members/${id}`, {
        headers: {
          Authorization: `Bearer ${session?.token}`,
        },
      });

      const json = await res.json();

      if (json.ok) {
        setMember(json.member);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadProducts() {
    try {
      const session = JSON.parse(localStorage.getItem("zedro_session"));
      const res = await fetch(`${API_URL}/api/admin/members/products/list`, {
        headers: {
          Authorization: `Bearer ${session?.token}`,
        },
      });

      const json = await res.json();

      if (json.ok) {
        setProducts(json.products);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function grantMembership() {
    const session = JSON.parse(localStorage.getItem("zedro_session"));
    const productId = selectedProduct;

    if (!productId) {
      alert("Selecciona un producto");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/members/${member.id}/assign-membership`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({
          product_id: productId,
          duration,
          source: "manual",
        }),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || "error");
      }

      alert("Membresía asignada");
      loadMember();
    } catch (err) {
      console.error(err);
      alert("Error asignando membresía");
    }
  }

  async function resetPassword() {
    try {
      setActionLoading(true);

      const session = JSON.parse(localStorage.getItem("zedro_session"));
      const res = await fetch(`${API_URL}/api/admin/members/${member.id}/reset-password`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session?.token}`,
        },
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || "error_reset_password");
      }

      alert("Correo de creación de contraseña enviado");
    } catch (err) {
      console.error(err);
      alert("Error enviando reset password");
    } finally {
      setActionLoading(false);
    }
  }

  async function resendActivation() {
    try {
      setActionLoading(true);

      const session = JSON.parse(localStorage.getItem("zedro_session"));
      const res = await fetch(`${API_URL}/api/admin/members/${member.id}/send-activation`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session?.token}`,
        },
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || "error_activation");
      }

      alert("Correo de activación reenviado");
    } catch (err) {
      console.error(err);
      alert("Error reenviando activación");
    } finally {
      setActionLoading(false);
    }
  }

  function normalizeStatus(subscription) {
    return String(subscription?.status || subscription?.membership_status || "").toLowerCase();
  }

  function normalizeAccessStatus(subscription) {
    return String(subscription?.access_status || "").toLowerCase();
  }

  function normalizeSource(subscription) {
    return String(subscription?.source || "").toLowerCase();
  }

  function isManualSubscription(subscription) {
    return normalizeSource(subscription) === "manual";
  }

  function getDateValue(value) {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return date;
  }

  function getStartDate(subscription) {
    return (
      subscription?.current_period_start ||
      subscription?.trial_start ||
      subscription?.created_at ||
      null
    );
  }

  function getEndDate(subscription) {
    return (
      subscription?.expires_at ||
      subscription?.current_period_end ||
      subscription?.trial_end ||
      null
    );
  }

  function hasFutureAccess(subscription) {
    const endDate = getDateValue(getEndDate(subscription));
    if (!endDate) return false;
    return endDate > new Date();
  }

  function isCancelScheduled(subscription) {
    if (!subscription) return false;

    return (
      subscription.cancel_at_period_end === true ||
      subscription.cancel_at_period_end === "true" ||
      normalizeAccessStatus(subscription) === "trial_canceling" ||
      normalizeAccessStatus(subscription) === "active_canceling"
    );
  }

  function isManualRevokedSubscription(subscription) {
    if (!subscription || !isManualSubscription(subscription)) return false;

    const status = normalizeStatus(subscription);
    const accessStatus = normalizeAccessStatus(subscription);

    return (
      status === "canceled" ||
      status === "cancelled" ||
      status === "revoked" ||
      status === "finished" ||
      accessStatus === "canceled" ||
      accessStatus === "cancelled" ||
      accessStatus === "revoked" ||
      accessStatus === "finished"
    );
  }

  function isPermanentSubscription(subscription) {
    if (!subscription) return false;

    const status = normalizeStatus(subscription);
    const accessStatus = normalizeAccessStatus(subscription);
    const source = normalizeSource(subscription);
    const duration = String(subscription.duration || subscription.access_duration || "").toLowerCase();

    return (
      status === "lifetime" ||
      status === "permanent" ||
      accessStatus === "lifetime" ||
      accessStatus === "permanent" ||
      duration === "permanent" ||
      (source === "manual" && !getEndDate(subscription)) ||
      isManualRevokedSubscription(subscription)
    );
  }

  function hasConvertedToMonthly(subscription) {
    if (!subscription) return false;

    const status = normalizeStatus(subscription);
    const trialEnd = getDateValue(subscription.trial_end);
    const currentPeriodEnd = getDateValue(subscription.current_period_end || subscription.expires_at);

    if (status === "active" || status === "past_due") return true;

    if (trialEnd && currentPeriodEnd) {
      return currentPeriodEnd.getTime() > trialEnd.getTime() + 60 * 1000;
    }

    return false;
  }

  function getSubscriptionType(subscription) {
    if (isPermanentSubscription(subscription)) return "Permanente";

    const status = normalizeStatus(subscription);

    if (
      status === "trialing" ||
      normalizeAccessStatus(subscription) === "trial_canceling" ||
      (subscription?.trial_end && !hasConvertedToMonthly(subscription))
    ) {
      return "Prueba 7";
    }

    return "Mensual";
  }

  function getEffectiveSubscriptionStatus(subscription) {
    if (!subscription) return "-";

    const status = normalizeStatus(subscription);
    const accessStatus = normalizeAccessStatus(subscription);
    const type = getSubscriptionType(subscription);

    if (isManualRevokedSubscription(subscription)) return "revoked";
    if (isPermanentSubscription(subscription)) return "permanent";

    if (status === "past_due" || accessStatus === "past_due") return "past_due";

    if (isCancelScheduled(subscription)) {
      if (type === "Prueba 7") return "trial_canceling";
      return "active_canceling";
    }

    if (status === "trialing") return "trialing";

    if (status === "active") {
      if (!getEndDate(subscription) || hasFutureAccess(subscription)) return "active";
      return "finished";
    }

    if (status === "canceled") {
      if (hasFutureAccess(subscription)) return "canceled";
      return "finished";
    }

    if (accessStatus === "paused") return "paused";
    if (accessStatus === "draft") return "draft";

    const endDate = getDateValue(getEndDate(subscription));
    if (endDate && endDate < new Date()) return "finished";

    return accessStatus || status || "-";
  }

  function renderSubscriptionStatus(subscription) {
    const effectiveStatus = getEffectiveSubscriptionStatus(subscription);

    if (effectiveStatus === "trialing") return "En prueba";
    if (effectiveStatus === "trial_canceling") return "Prueba cancelada";
    if (effectiveStatus === "active") return "Activa";
    if (effectiveStatus === "active_canceling") return "Cancelación programada";
    if (effectiveStatus === "permanent") return "Permanente";
    if (effectiveStatus === "revoked") return "Revocado";
    if (effectiveStatus === "finished") return "Finalizada";
    if (effectiveStatus === "canceled") return "Cancelada";
    if (effectiveStatus === "past_due") return "Pago pendiente";
    if (effectiveStatus === "paused") return "Pausada";
    if (effectiveStatus === "draft") return "Borrador";

    return effectiveStatus;
  }

  function getMemberCurrentAccessLabel() {
    const subscriptions = Array.isArray(member?.subscriptions) ? member.subscriptions : [];

    const current = subscriptions.find((subscription) => {
      const status = getEffectiveSubscriptionStatus(subscription);
      return ["active", "trialing", "active_canceling", "trial_canceling", "past_due", "permanent"].includes(status);
    });

    if (current) {
      return `${getSubscriptionType(current)} · ${renderSubscriptionStatus(current)}`;
    }

    const hasHistoricalAccess = subscriptions.length > 0;
    const rawAccess = String(member?.access_status || member?.membership_status || "").toLowerCase();

    if (["canceled", "cancelled", "finished", "expired", "inactive", "paused"].includes(rawAccess)) {
      return hasHistoricalAccess ? "Sin acceso activo" : "Sin membresía";
    }

    if (rawAccess === "past_due") return "Pago pendiente";
    if (rawAccess === "active") return "Activa";
    if (rawAccess === "trialing") return "En prueba";

    if (member?.access_status || member?.membership_status) {
      return member.access_status || member.membership_status;
    }

    return hasHistoricalAccess ? "Sin acceso activo" : "-";
  }

  function getTypeBadgeStyle(subscription) {
    const type = getSubscriptionType(subscription);

    if (type === "Permanente") {
      return {
        ...typeBadgeStyle,
        background: "#f7f3ea",
        color: "#8a6424",
        border: "1px solid rgba(199, 154, 69, 0.35)",
      };
    }

    if (type === "Prueba 7") {
      return {
        ...typeBadgeStyle,
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "1px solid #bfdbfe",
      };
    }

    return {
      ...typeBadgeStyle,
      background: "#eefaf3",
      color: "#166534",
      border: "1px solid #bbf7d0",
    };
  }

  function getSubscriptionStatusStyle(subscription) {
    const effectiveStatus = getEffectiveSubscriptionStatus(subscription);

    if (effectiveStatus === "active" || effectiveStatus === "permanent") {
      return {
        ...statusBadgeStyle,
        background: "#eefaf3",
        color: "#166534",
        border: "1px solid #bbf7d0",
      };
    }

    if (effectiveStatus === "trialing") {
      return {
        ...statusBadgeStyle,
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "1px solid #bfdbfe",
      };
    }

    if (effectiveStatus === "trial_canceling" || effectiveStatus === "active_canceling") {
      return {
        ...statusBadgeStyle,
        background: "#fff7ed",
        color: "#9a3412",
        border: "1px solid #fed7aa",
      };
    }

    if (effectiveStatus === "revoked") {
      return {
        ...statusBadgeStyle,
        background: "#f8fafc",
        color: "#334155",
        border: "1px solid #cbd5e1",
      };
    }

    if (effectiveStatus === "finished" || effectiveStatus === "canceled") {
      return {
        ...statusBadgeStyle,
        background: "#f8fafc",
        color: "#475569",
        border: "1px solid #e2e8f0",
      };
    }

    if (effectiveStatus === "past_due") {
      return {
        ...statusBadgeStyle,
        background: "#fef2f2",
        color: "#b42318",
        border: "1px solid #fecaca",
      };
    }

    return {
      ...statusBadgeStyle,
      background: "#f3f4f6",
      color: "#374151",
      border: "1px solid #e5e7eb",
    };
  }

  function formatDate(value) {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("es-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  function formatDateTime(value) {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("es-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function formatDuration(durationMs) {
    const value = Number(durationMs);

    if (!Number.isFinite(value) || value < 0) return "-";
    if (value < 1000) return `${Math.round(value)} ms`;

    return `${(value / 1000).toFixed(1)} s`;
  }

  function renderTraceStatus(status) {
    const value = String(status || "").toLowerCase();

    if (value === "success") return "Correcta";
    if (value === "failed") return "Fallida";
    if (value === "timeout") return "Tiempo agotado";
    if (value === "rejected") return "Rechazada";
    if (value === "limit_reached") return "Límite alcanzado";
    if (value === "started") return "Procesando";

    return status || "-";
  }

  function getTraceStatusStyle(status) {
    const value = String(status || "").toLowerCase();

    if (value === "success") {
      return {
        ...traceStatusBadgeStyle,
        background: "#eefaf3",
        color: "#166534",
        border: "1px solid #bbf7d0",
      };
    }

    if (value === "timeout") {
      return {
        ...traceStatusBadgeStyle,
        background: "#fff7ed",
        color: "#9a3412",
        border: "1px solid #fed7aa",
      };
    }

    if (value === "failed") {
      return {
        ...traceStatusBadgeStyle,
        background: "#fef2f2",
        color: "#b42318",
        border: "1px solid #fecaca",
      };
    }

    if (value === "limit_reached" || value === "rejected") {
      return {
        ...traceStatusBadgeStyle,
        background: "#f8fafc",
        color: "#475569",
        border: "1px solid #dbe4ef",
      };
    }

    return {
      ...traceStatusBadgeStyle,
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    };
  }

  if (loading) {
    return <div style={{ padding: 40 }}>Cargando miembro...</div>;
  }

  if (!member) {
    return <div style={{ padding: 40 }}>Miembro no encontrado</div>;
  }

  const connectionUsage = member?.platform_usage?.connection || {
    daily_limit: 3,
    used_today: 0,
    remaining_today: 3,
    total_usage_records: 0,
    failed_without_charge: 0,
    last_activity_at: null,
    tracking_available: false,
    recent_requests: [],
  };

  const recentConnectionRequests = Array.isArray(
    connectionUsage.recent_requests
  )
    ? connectionUsage.recent_requests
    : [];

  return (
    <>
      <AdminHeader />

      <div className="admin-page" style={{ maxWidth: 960, margin: "0 auto" }}>
        <button onClick={() => navigate("/admin/miembros")} style={backBtnStyle}>
          ← Volver a CRM
        </button>

        <h2 style={pageTitleStyle}>CRM 360°</h2>

        <section style={memberSummaryStyle}>
          <div style={memberHeaderStyle}>
            <div>
              <div style={summaryLabelStyle}>Usuario seleccionado</div>
              <h3 style={memberNameStyle}>{member.name || "Sin nombre registrado"}</h3>
              <div style={memberEmailStyle}>{member.email}</div>
            </div>

            <div style={roleBadgeStyle}>{member.role || "-"}</div>
          </div>

          <div style={summaryListStyle}>
            <SummaryItem label="Acceso actual" value={getMemberCurrentAccessLabel()} strong />
            <SummaryItem label="Lecciones completadas" value={member.completed_lessons || 0} />
            <SummaryItem label="Sistema actual" value={member.system_title || "-"} />
            <SummaryItem label="Progreso Core" value={`${member.progress || 0}%`} />
          </div>
        </section>

        <h3 style={sectionHeadingStyle}>
          Uso de la plataforma
        </h3>

        <section style={platformUsageCardStyle}>
          <div style={platformUsageHeaderStyle}>
            <div>
              <div style={platformResourceLabelStyle}>Recurso</div>
              <h4 style={platformResourceTitleStyle}>Conexión</h4>
              <p style={platformResourceDescriptionStyle}>
                Actividad de la asistencia para preparar oraciones.
              </p>
            </div>

            <span
              style={
                connectionUsage.tracking_available
                  ? platformResourceBadgeStyle
                  : platformResourcePendingBadgeStyle
              }
            >
              {connectionUsage.tracking_available
                ? "Seguimiento activo"
                : "Detalle pendiente"}
            </span>
          </div>

          <div style={platformMetricsGridStyle}>
            <UsageMetric
              label="Oraciones preparadas hoy"
              value={`${connectionUsage.used_today || 0} de ${
                connectionUsage.daily_limit || 3
              }`}
              strong
            />
            <UsageMetric
              label="Disponibles hoy"
              value={connectionUsage.remaining_today ?? 3}
            />
            <UsageMetric
              label="Usos registrados"
              value={connectionUsage.total_usage_records || 0}
            />
            <UsageMetric
              label="Fallos sin descuento"
              value={connectionUsage.failed_without_charge || 0}
            />
          </div>

          <div style={platformUsageFooterStyle}>
            <div>
              <span style={platformLastActivityLabelStyle}>
                Última actividad
              </span>
              <strong style={platformLastActivityValueStyle}>
                {formatDateTime(connectionUsage.last_activity_at)}
              </strong>
            </div>

            <button
              type="button"
              onClick={() =>
                setShowConnectionHistory((current) => !current)
              }
              style={historyToggleButtonStyle}
              aria-expanded={showConnectionHistory}
            >
              {showConnectionHistory
                ? "Ocultar historial"
                : "Ver historial"}
            </button>
          </div>

          {showConnectionHistory && (
            <div style={historyPanelStyle}>
              {!connectionUsage.tracking_available ? (
                <p style={historyEmptyStyle}>
                  La trazabilidad técnica todavía no está disponible. La
                  información general de uso permanece visible y la ficha del
                  usuario continúa funcionando normalmente.
                </p>
              ) : recentConnectionRequests.length === 0 ? (
                <p style={historyEmptyStyle}>
                  Aún no hay solicitudes de Conexión registradas desde la
                  activación de la trazabilidad.
                </p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      minWidth: "820px",
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={historyThStyle}>Fecha y hora</th>
                        <th style={historyThStyle}>Resultado</th>
                        <th style={historyThStyle}>Duración</th>
                        <th style={historyThStyle}>Oportunidad usada</th>
                        <th style={historyThStyle}>Código de soporte</th>
                      </tr>
                    </thead>

                    <tbody>
                      {recentConnectionRequests.map((request) => (
                        <tr key={request.request_id}>
                          <td style={historyTdStyle}>
                            {formatDateTime(request.started_at)}
                          </td>
                          <td style={historyTdStyle}>
                            <span
                              style={getTraceStatusStyle(request.status)}
                            >
                              {renderTraceStatus(request.status)}
                            </span>

                            {request.error_code && (
                              <div style={traceErrorCodeStyle}>
                                {request.error_code}
                              </div>
                            )}
                          </td>
                          <td style={historyTdStyle}>
                            {formatDuration(request.duration_ms)}
                          </td>
                          <td style={historyTdStyle}>
                            {request.opportunity_consumed ? "Sí" : "No"}
                          </td>
                          <td style={historyTdStyle}>
                            <code style={supportCodeStyle}>
                              {request.support_id || "-"}
                            </code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <p style={historyPrivacyNoteStyle}>
                Esta vista conserva únicamente metadatos técnicos. No guarda
                lo que la persona escribió ni el contenido de su oración.
              </p>
            </div>
          )}
        </section>

        <h3 style={sectionHeadingStyle}>
          Productos y accesos
        </h3>

        {member.subscriptions?.length === 0 ? (
          <p style={emptyTextStyle}>No hay productos ni accesos asociados.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "760px" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Producto</th>
                  <th style={thStyle}>Tipo</th>
                  <th style={thStyle}>Estado</th>
                  <th style={thStyle}>Fuente</th>
                  <th style={thStyle}>Inicio</th>
                  <th style={thStyle}>Expira</th>
                </tr>
              </thead>

              <tbody>
                {member.subscriptions.map((s) => (
                  <tr key={s.id}>
                    <td style={tdStyle}>{s.product_name || "-"}</td>
                    <td style={tdStyle}>
                      <span style={getTypeBadgeStyle(s)}>
                        {getSubscriptionType(s)}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={getSubscriptionStatusStyle(s)}>
                        {renderSubscriptionStatus(s)}
                      </span>
                    </td>
                    <td style={tdStyle}>{renderSource(s.source)}</td>
                    <td style={tdStyle}>{formatDate(getStartDate(s))}</td>
                    <td style={tdStyle}>
                      {isPermanentSubscription(s) && !isManualRevokedSubscription(s)
                        ? "Sin expiración"
                        : formatDate(getEndDate(s))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={boxStyle}>
          <div style={sectionTitle}>Seguridad</div>

          <div style={rowStyle}>
            <button onClick={resetPassword} disabled={actionLoading} style={btnStyle}>
              Reset password
            </button>

            <button onClick={resendActivation} disabled={actionLoading} style={btnStyle}>
              Reenviar activación
            </button>
          </div>

          <div style={sectionTitle}>Gestión</div>

          <div style={rowStyle}>
            <button type="button" onClick={() => navigate("/admin/suscripciones")} style={btnStyle}>
              Ver suscripción
            </button>

            <button
              type="button"
              disabled
              style={{ ...btnStyle, opacity: 0.55, cursor: "not-allowed" }}
              title="Esta vista se conectará más adelante al progreso completo del miembro."
            >
              Ver progreso completo — próximamente
            </button>
          </div>

          <div style={sectionTitle}>Membresía</div>

          <div style={rowStyle}>
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              style={{ ...selectStyle, width: "100%", boxSizing: "border-box" }}
            >
              <option value="">Seleccionar producto</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              style={{ ...selectStyle, width: "100%", boxSizing: "border-box" }}
            >
              <option value="1_month">1 mes</option>
              <option value="3_months">3 meses</option>
              <option value="6_months">6 meses</option>
              <option value="permanent">Permanente</option>
            </select>

            <button onClick={grantMembership} style={btnStyle}>
              Asignar membresía
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function UsageMetric({ label, value, strong }) {
  return (
    <div style={usageMetricStyle}>
      <span style={usageMetricLabelStyle}>{label}</span>
      <span
        style={
          strong
            ? usageMetricValueStrongStyle
            : usageMetricValueStyle
        }
      >
        {value}
      </span>
    </div>
  );
}

function SummaryItem({ label, value, strong }) {
  return (
    <div style={summaryItemStyle}>
      <span style={summaryItemLabelStyle}>{label}</span>
      <span style={strong ? summaryItemValueStrongStyle : summaryItemValueStyle}>
        {value}
      </span>
    </div>
  );
}

function renderSource(source) {
  const value = String(source || "").toLowerCase();

  if (value === "stripe") return "Stripe";
  if (value === "manual") return "Manual";

  return source || "-";
}

const backBtnStyle = {
  marginBottom: 10,
  background: "transparent",
  border: "none",
  color: "#2c3b51",
  fontWeight: 700,
  cursor: "pointer",
};

const pageTitleStyle = {
  margin: "0 0 18px",
  color: "#0f2f46",
  fontSize: 28,
  letterSpacing: "-0.03em",
};

const memberSummaryStyle = {
  marginTop: 8,
  marginBottom: 26,
  padding: "20px 22px",
  borderRadius: 18,
  background: "#ffffff",
  border: "1px solid #e6ebf1",
  boxShadow: "0 16px 36px rgba(15, 47, 70, 0.06)",
};

const memberHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 16,
  alignItems: "flex-start",
  paddingBottom: 16,
  borderBottom: "1px solid #eef2f7",
};

const summaryLabelStyle = {
  marginBottom: 6,
  color: "#c79a45",
  fontSize: 12,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const memberNameStyle = {
  margin: 0,
  color: "#0f2f46",
  fontSize: 22,
  fontWeight: 800,
  letterSpacing: "-0.03em",
};

const memberEmailStyle = {
  marginTop: 6,
  color: "#64748b",
  fontSize: 14,
  wordBreak: "break-word",
};

const roleBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 86,
  padding: "6px 10px",
  borderRadius: 999,
  background: "#f8fafc",
  border: "1px solid #dbe4ef",
  color: "#0f2f46",
  fontSize: 12,
  fontWeight: 900,
  textTransform: "uppercase",
};

const summaryListStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  columnGap: 26,
  rowGap: 14,
  paddingTop: 16,
};

const summaryItemStyle = {
  display: "grid",
  gap: 4,
};

const summaryItemLabelStyle = {
  color: "#64748b",
  fontSize: 12,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const summaryItemValueStyle = {
  color: "#26364a",
  fontSize: 14,
  fontWeight: 700,
  wordBreak: "break-word",
};

const summaryItemValueStrongStyle = {
  ...summaryItemValueStyle,
  color: "#0f2f46",
  fontWeight: 900,
};

const sectionHeadingStyle = {
  marginTop: 30,
  marginBottom: 12,
  color: "#0f2f46",
  fontSize: 18,
  fontWeight: 800,
};

const emptyTextStyle = {
  color: "#66758a",
  fontSize: 14,
};

const thStyle = {
  textAlign: "left",
  padding: "10px",
  borderBottom: "1px solid #e6ebf1",
  color: "#64748b",
  fontSize: 12,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const tdStyle = {
  padding: "10px",
  borderBottom: "1px solid #f3f3f3",
  color: "#2c3b51",
  fontSize: 13,
  verticalAlign: "middle",
};

const boxStyle = {
  marginTop: 30,
  padding: 20,
  border: "1px solid #e6ebf1",
  borderRadius: 14,
  background: "#fafafa",
};

const sectionTitle = {
  fontWeight: 800,
  marginTop: 16,
  marginBottom: 8,
  color: "#0f2f46",
};

const rowStyle = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
};

const btnStyle = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #d7dee8",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
  color: "#2c3b51",
};

const selectStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #d7dee8",
};

const typeBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 74,
  padding: "4px 9px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800,
  whiteSpace: "nowrap",
};

const statusBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 92,
  padding: "4px 9px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800,
  whiteSpace: "nowrap",
};

const platformUsageCardStyle = {
  padding: "20px 22px",
  borderRadius: 18,
  background: "#ffffff",
  border: "1px solid #e6ebf1",
  boxShadow: "0 14px 32px rgba(15, 47, 70, 0.045)",
};

const platformUsageHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  paddingBottom: 16,
  borderBottom: "1px solid #eef2f7",
};

const platformResourceLabelStyle = {
  marginBottom: 5,
  color: "#c79a45",
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const platformResourceTitleStyle = {
  margin: 0,
  color: "#0f2f46",
  fontSize: 20,
  fontWeight: 900,
  letterSpacing: "-0.025em",
};

const platformResourceDescriptionStyle = {
  margin: "5px 0 0",
  color: "#64748b",
  fontSize: 13,
  lineHeight: 1.5,
};

const platformResourceBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "6px 10px",
  borderRadius: 999,
  background: "#eefaf3",
  border: "1px solid #bbf7d0",
  color: "#166534",
  fontSize: 11,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const platformResourcePendingBadgeStyle = {
  ...platformResourceBadgeStyle,
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  color: "#9a3412",
};

const platformMetricsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))",
  gap: 12,
  paddingTop: 16,
};

const usageMetricStyle = {
  display: "grid",
  gap: 5,
  minHeight: 74,
  padding: "13px 14px",
  borderRadius: 14,
  background: "#f8fafc",
  border: "1px solid #edf1f5",
};

const usageMetricLabelStyle = {
  color: "#64748b",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.035em",
  lineHeight: 1.35,
};

const usageMetricValueStyle = {
  color: "#26364a",
  fontSize: 18,
  fontWeight: 800,
};

const usageMetricValueStrongStyle = {
  ...usageMetricValueStyle,
  color: "#0f2f46",
  fontSize: 20,
  fontWeight: 900,
};

const platformUsageFooterStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
  marginTop: 16,
  paddingTop: 14,
  borderTop: "1px solid #eef2f7",
};

const platformLastActivityLabelStyle = {
  display: "block",
  marginBottom: 4,
  color: "#64748b",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const platformLastActivityValueStyle = {
  color: "#26364a",
  fontSize: 13,
};

const historyToggleButtonStyle = {
  minHeight: 40,
  padding: "0 14px",
  borderRadius: 999,
  border: "1px solid #d7dee8",
  background: "#fff",
  color: "#0f2f46",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 900,
};

const historyPanelStyle = {
  marginTop: 16,
  paddingTop: 16,
  borderTop: "1px solid #eef2f7",
};

const historyEmptyStyle = {
  margin: 0,
  padding: "12px 14px",
  borderRadius: 12,
  background: "#f8fafc",
  color: "#64748b",
  fontSize: 13,
  lineHeight: 1.55,
};

const historyThStyle = {
  textAlign: "left",
  padding: "10px",
  borderBottom: "1px solid #e6ebf1",
  color: "#64748b",
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.035em",
};

const historyTdStyle = {
  padding: "11px 10px",
  borderBottom: "1px solid #f0f3f6",
  color: "#2c3b51",
  fontSize: 12,
  verticalAlign: "top",
};

const traceStatusBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 88,
  padding: "4px 8px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const traceErrorCodeStyle = {
  marginTop: 5,
  color: "#7b8797",
  fontSize: 10,
  fontFamily: "monospace",
  wordBreak: "break-word",
};

const supportCodeStyle = {
  color: "#0f2f46",
  fontSize: 11,
  fontWeight: 800,
  whiteSpace: "nowrap",
};

const historyPrivacyNoteStyle = {
  margin: "12px 0 0",
  color: "#7b8797",
  fontSize: 11,
  lineHeight: 1.5,
};

